import {
  type WorldPos,
  type WorldVector3D,
  vector3D,
  vector3DLength,
  normalize3D,
  dot3D,
  clamp,
} from './measureUtils';

export interface ImageCandidate {
  id: number;
  name: string;
  seq: number;
  frame: number;
  cam: number;
  imagePath?: string;
  pos?: number[];
  worldPos: WorldPos;
  worldFwd: WorldVector3D;
}

export interface RankedImageCandidate extends ImageCandidate {
  distanceMeters: number;
  directionScore: number;
  score: number;
  forwardDistanceMeters: number;
  offAxisDistanceMeters: number;
  offAxisScore: number;
  distanceScore: number;
  forwardScore: number;
  frameDistance?: number;
  frameDistanceScore?: number;
}

export interface RankImagesOptions {
  topK?: number;
  maxDistanceMeters?: number;
  minDirectionDot?: number;
  diversifyByFrame?: boolean;
  preferredDistanceMeters?: number;
  distanceFalloffMeters?: number;

  maxOffAxisMeters?: number;
  minForwardDistanceMeters?: number;
  maxForwardDistanceMeters?: number;
}

const DEFAULT_OPTIONS: Required<RankImagesOptions> = {
  topK: 6,
  maxDistanceMeters: 120,
  minDirectionDot: 0.15,
  diversifyByFrame: true,
  preferredDistanceMeters: 12,
  distanceFalloffMeters: 18,

  maxOffAxisMeters: 12,
  minForwardDistanceMeters: 1.5,
  maxForwardDistanceMeters: 40,
};

function smoothstep01(x: number): number {
  const t = clamp(x, 0, 1);
  return t * t * (3 - 2 * t);
}

function preferredDistanceScore(
  distanceMeters: number,
  preferredDistanceMeters: number,
  falloffMeters: number
): number {
  const delta = Math.abs(distanceMeters - preferredDistanceMeters);
  return 1 - smoothstep01(delta / Math.max(falloffMeters, 1e-6));
}

function forwardBandScore(
  forwardDistanceMeters: number,
  minForwardDistanceMeters: number,
  maxForwardDistanceMeters: number
): number {
  if (forwardDistanceMeters < minForwardDistanceMeters) return 0;
  if (forwardDistanceMeters > maxForwardDistanceMeters) return 0;

  const normalized =
    (forwardDistanceMeters - minForwardDistanceMeters) /
    Math.max(maxForwardDistanceMeters - minForwardDistanceMeters, 1e-6);

  return 1 - smoothstep01(normalized);
}

function computePointToRayMetrics(
  cameraPos: WorldPos,
  cameraFwd: WorldVector3D,
  point: WorldPos
): {
  distanceMeters: number;
  forwardDistanceMeters: number;
  offAxisDistanceMeters: number;
  directionDotFromCamera: number;
} | null {
  const toPoint = vector3D(cameraPos, point);
  const distanceMeters = vector3DLength(toPoint);
  if (distanceMeters <= 1e-6) return null;

  const toPointDir = normalize3D(toPoint);
  const fwdDir = normalize3D(cameraFwd);
  if (!toPointDir || !fwdDir) return null;

  const directionDotFromCamera = dot3D(fwdDir, toPointDir);
  const forwardDistanceMeters = dot3D(toPoint, fwdDir);

  const offAxisSq = Math.max(
    0,
    distanceMeters * distanceMeters - forwardDistanceMeters * forwardDistanceMeters
  );
  const offAxisDistanceMeters = Math.sqrt(offAxisSq);

  return {
    distanceMeters,
    forwardDistanceMeters,
    offAxisDistanceMeters,
    directionDotFromCamera,
  };
}

function dedupeByFrameKeepingBest(
  ranked: RankedImageCandidate[]
): RankedImageCandidate[] {
  const bestByFrame = new Map<number, RankedImageCandidate>();

  for (const candidate of ranked) {
    const existing = bestByFrame.get(candidate.frame);
    if (!existing || candidate.score > existing.score) {
      bestByFrame.set(candidate.frame, candidate);
    }
  }

  return Array.from(bestByFrame.values()).sort((a, b) => b.score - a.score);
}

export function rankImagesForPoint(
  clickedPoint: WorldPos,
  candidates: ImageCandidate[],
  options: RankImagesOptions = {}
): RankedImageCandidate[] {
  const cfg = { ...DEFAULT_OPTIONS, ...options };

  const ranked: RankedImageCandidate[] = [];

  for (const candidate of candidates) {
    const metrics = computePointToRayMetrics(
      candidate.worldPos,
      candidate.worldFwd,
      clickedPoint
    );

    if (!metrics) continue;

    const {
      distanceMeters,
      forwardDistanceMeters,
      offAxisDistanceMeters,
      directionDotFromCamera,
    } = metrics;

    if (distanceMeters > cfg.maxDistanceMeters) continue;
    if (directionDotFromCamera < cfg.minDirectionDot) continue;
    if (forwardDistanceMeters < cfg.minForwardDistanceMeters) continue;
    if (forwardDistanceMeters > cfg.maxForwardDistanceMeters) continue;
    if (offAxisDistanceMeters > cfg.maxOffAxisMeters) continue;

    const directionScore = smoothstep01(
      (directionDotFromCamera - cfg.minDirectionDot) /
        Math.max(1 - cfg.minDirectionDot, 1e-6)
    );

    const offAxisScore = 1 - smoothstep01(
      offAxisDistanceMeters / Math.max(cfg.maxOffAxisMeters, 1e-6)
    );

    const distanceScore = preferredDistanceScore(
      distanceMeters,
      cfg.preferredDistanceMeters,
      cfg.distanceFalloffMeters
    );

    const forwardScore = forwardBandScore(
      forwardDistanceMeters,
      cfg.minForwardDistanceMeters,
      cfg.maxForwardDistanceMeters
    );

    const score =
      directionScore * 0.40 +
      offAxisScore * 0.30 +
      distanceScore * 0.20 +
      forwardScore * 0.10;

    ranked.push({
      ...candidate,
      distanceMeters,
      directionScore,
      score,
      forwardDistanceMeters,
      offAxisDistanceMeters,
      offAxisScore,
      distanceScore,
      forwardScore,
    });
  }

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.offAxisDistanceMeters !== b.offAxisDistanceMeters) {
      return a.offAxisDistanceMeters - b.offAxisDistanceMeters;
    }
    return a.distanceMeters - b.distanceMeters;
  });

  const finalRanked = cfg.diversifyByFrame
    ? dedupeByFrameKeepingBest(ranked)
    : ranked;

  return finalRanked.slice(0, cfg.topK);
}

function pickSeedDiverse(
  ranked: RankedImageCandidate[],
  topK: number
): RankedImageCandidate[] {
  const picked: RankedImageCandidate[] = [];

  for (const item of ranked) {
    if (picked.length >= topK) break;

    const nearDuplicate = picked.some((p) => {
      const sameCam = p.cam === item.cam;
      const closeFrame = Math.abs(p.frame - item.frame) <= 2;
      return sameCam && closeFrame;
    });

    if (nearDuplicate) continue;
    picked.push(item);
  }

  return picked.slice(0, topK);
}

/**
 * Step 1:
 * Build 36 diverse seed candidates for the user to choose from.
 * First try strict settings. If too few results, fall back to relaxed settings.
 */
export function selectSeedCandidates(
  clickedPoint: WorldPos,
  candidates: ImageCandidate[],
  topK = 36
): RankedImageCandidate[] {
  const strictRanked = rankImagesForPoint(clickedPoint, candidates, {
    topK: 400,
    maxDistanceMeters: 28,
    minDirectionDot: 0.35,
    diversifyByFrame: false,
    preferredDistanceMeters: 8,
    distanceFalloffMeters: 8,
    maxOffAxisMeters: 8,
    minForwardDistanceMeters: 2,
    maxForwardDistanceMeters: 22,
  });

  let ranked = strictRanked;
  let mode: 'strict' | 'relaxed' = 'strict';

  if (strictRanked.length < Math.min(topK, 8)) {
    const relaxedRanked = rankImagesForPoint(clickedPoint, candidates, {
      topK: 400,
      maxDistanceMeters: 45,
      minDirectionDot: 0.15,
      diversifyByFrame: false,
      preferredDistanceMeters: 10,
      distanceFalloffMeters: 14,
      maxOffAxisMeters: 14,
      minForwardDistanceMeters: 1,
      maxForwardDistanceMeters: 35,
    });

    ranked = relaxedRanked;
    mode = 'relaxed';
  }

  console.log(
    `[SourceImages][Seeds] ranked pool top 40 (${mode}):`,
    ranked.slice(0, 40).map((item) => ({
      id: item.id,
      name: item.name,
      cam: item.cam,
      frame: item.frame,
      score: Number(item.score.toFixed(3)),
      distanceMeters: Number(item.distanceMeters.toFixed(2)),
      directionScore: Number(item.directionScore.toFixed(3)),
      forwardDistanceMeters: Number(item.forwardDistanceMeters.toFixed(2)),
      offAxisDistanceMeters: Number(item.offAxisDistanceMeters.toFixed(2)),
    }))
  );

  const picked = pickSeedDiverse(ranked, topK);

  console.log(
    `[SourceImages][Seeds] picked seeds (${mode}):`,
    picked.map((item, idx) => ({
      rank: idx + 1,
      id: item.id,
      name: item.name,
      cam: item.cam,
      frame: item.frame,
      score: Number(item.score.toFixed(3)),
      distanceMeters: Number(item.distanceMeters.toFixed(2)),
      directionScore: Number(item.directionScore.toFixed(3)),
      forwardDistanceMeters: Number(item.forwardDistanceMeters.toFixed(2)),
      offAxisDistanceMeters: Number(item.offAxisDistanceMeters.toFixed(2)),
    }))
  );

  return picked;
}

/**
 * Step 2:
 * User picked one seed image. Expand from that same camera.
 * First try stricter same-camera ranking. If too sparse, relax it.
 */
export function expandFromSeedCandidate(
  clickedPoint: WorldPos,
  sameCameraCandidates: ImageCandidate[],
  seed: ImageCandidate,
  topK = 20
): RankedImageCandidate[] {
  const strictRanked = rankImagesForPoint(clickedPoint, sameCameraCandidates, {
    topK: 500,
    maxDistanceMeters: 60,
    minDirectionDot: 0.15,
    diversifyByFrame: false,
    preferredDistanceMeters: 10,
    distanceFalloffMeters: 16,
    maxOffAxisMeters: 14,
    minForwardDistanceMeters: 1.5,
    maxForwardDistanceMeters: 40,
  });

  let ranked = strictRanked;
  let mode: 'strict' | 'relaxed' = 'strict';

  if (strictRanked.length < Math.min(topK, 8)) {
    const relaxedRanked = rankImagesForPoint(clickedPoint, sameCameraCandidates, {
      topK: 500,
      maxDistanceMeters: 90,
      minDirectionDot: 0.0,
      diversifyByFrame: false,
      preferredDistanceMeters: 12,
      distanceFalloffMeters: 24,
      maxOffAxisMeters: 22,
      minForwardDistanceMeters: 0.5,
      maxForwardDistanceMeters: 55,
    });

    ranked = relaxedRanked;
    mode = 'relaxed';
  }

  const rescored = ranked.map((item) => {
    const frameDistance = Math.abs(item.frame - seed.frame);
    const frameDistanceScore = 1 - smoothstep01(frameDistance / 20);

    const score =
      item.score * 0.75 +
      frameDistanceScore * 0.25;

    return {
      ...item,
      score,
      frameDistance,
      frameDistanceScore,
    };
  });

  rescored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if ((a.frameDistance ?? 9999) !== (b.frameDistance ?? 9999)) {
      return (a.frameDistance ?? 9999) - (b.frameDistance ?? 9999);
    }
    return a.distanceMeters - b.distanceMeters;
  });

  const deduped = dedupeByFrameKeepingBest(rescored);

  console.log('[SourceImages][Expand] seed:', {
    id: seed.id,
    name: seed.name,
    cam: seed.cam,
    frame: seed.frame,
  });

  console.log(
    `[SourceImages][Expand] expanded top 40 (${mode}):`,
    deduped.slice(0, 40).map((item) => ({
      id: item.id,
      name: item.name,
      cam: item.cam,
      frame: item.frame,
      score: Number(item.score.toFixed(3)),
      frameDistance: item.frameDistance ?? null,
      distanceMeters: Number(item.distanceMeters.toFixed(2)),
      directionScore: Number(item.directionScore.toFixed(3)),
      forwardDistanceMeters: Number(item.forwardDistanceMeters.toFixed(2)),
      offAxisDistanceMeters: Number(item.offAxisDistanceMeters.toFixed(2)),
    }))
  );

  return deduped.slice(0, topK);
}